/*
 *
 * ChannelPage Form
 *
 */

import React, { useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { toast } from "sonner";
import { t } from "copy/t";
import { useMinGuestAgeServerError, minGuestAgeErrorMessage } from "./minGuestAgeError";
import { InlineNote, saveErrorMessage } from "./InlineNote";
// import { reduxForm } from 'redux-form';

import { createStructuredSelector } from "reselect";
import { mapIsEmpty } from "utils/helpers";
import {
  selectClients,
  selectCurrentTab,
  selectIsFetching,
  selectChannelThemes,
  selectModes,
  selectChannelErrors,
  selectChannelSlugIsValid,
  selectSaveError,
  selectIsSaving,
} from "../selectors";
import {
  fetchClientsRequest,
  fetchThemesRequest,
  fetchModesRequest,
  clearSaveError,
  changeTab,
} from "../actions";
import BasicInfoForm from "./BasicInfoForm";
import PhotographerThemeForm from "./PhotographerThemeForm";
import EnterpriseThemeForm from "./EnterpriseThemeForm";
import LogoForm from "./LogoForm";
import PhotoOptionsForm, { captureModes } from "./PhotoOptionsForm";
import {
  persistedModeIds,
  ownsChannel,
  submittedModeIds,
  selectedModeShortnames,
  aiModeSelected,
  consentAckBlocksSave,
} from "./photoOptionsState";
import ShareOptionsForm from './ShareOptionsForm';
import BrandingForm from "./BrandingForm";
import MessagingOptionForm from "./MessagingOptionForm";
import {
  selectIsPhotographer,
  selectEnterpriseAdmins,
} from "containers/App/selectors";
import { setModal } from "containers/RootModal/actions";
import { COLOR_PICKER } from "containers/RootModal/constants";
import FormTabs from "components/FormTabs";
import TermsForm from "./TermsForm";
import withRouter from "withRouter";
import { useForm, useWatch } from "react-hook-form";

// The tab order per audience, extracted so the shared-submit guard below can
// derive the Photos tab's index from the SAME source BaseForm renders from.
const PHOTOGRAPHER_TAB_NAMES = [
  "BASIC_INFO",
  "PHOTO_THEME",
  "LOGO_THEME",
  "PHOTO_OPTIONS",
  "SHARE_OPTIONS",
];
const ENTERPRISE_TAB_NAMES = [
  "BASIC_INFO",
  "BRANDING",
  "PHOTO_OPTIONS",
  "SHARE_OPTIONS",
  "MESSAGING",
  "TERMS",
];
const tabNamesFor = (isPhotographer, useMoveboothTheme) =>
  isPhotographer || useMoveboothTheme
    ? PHOTOGRAPHER_TAB_NAMES
    : ENTERPRISE_TAB_NAMES;

// A failed save lands on the first tab, except a minimum-guest-age rejection,
// whose inline alert lives on the Sharing tab. Exported for unit testing.
export const errorTabIndex = (errors, isPhotographer, useMoveboothTheme) => {
  if (!minGuestAgeErrorMessage(errors)) return 0;
  return tabNamesFor(isPhotographer, useMoveboothTheme).indexOf("SHARE_OPTIONS");
};

/**
 * Wrap the shared channel-form `onSubmit` so that ANY tab's Save — not only
 * the Photos tab's own button — refuses to persist a newly-selected AI
 * capture mode until the operator has acknowledged the BIPA consent
 * requirement. Every tab wires its <form> to `handleSubmit(onSubmit)` with
 * this same callback (see EditChannel's submitChannel note), so guarding here
 * covers sibling-tab submits that never render the consent notice. On block
 * nothing is saved; the operator is routed to the Photos tab, where the
 * notice and its disabled Save explain the state. A channel with AI already
 * persisted, or any submit without AI selected, passes straight through.
 * Exported for direct unit testing.
 */
export const guardChannelSubmit =
  ({ onSubmit, modes, aiPersisted, consentAcked, goToPhotosTab }) =>
  (values, ...rest) => {
    const aiSelected = aiModeSelected(
      selectedModeShortnames(captureModes, modes, submittedModeIds(values))
    );
    if (
      consentAckBlocksSave({ aiSelected, aiPersisted, acked: consentAcked })
    ) {
      toast.error(t("toast.channel.aiConsentRequired"));
      goToPhotosTab();
      return undefined;
    }
    return onSubmit(values, ...rest);
  };

class BaseForm extends React.PureComponent {
  constructor(props) {
    super(props);
    this.state = { themeIdForNew: null };
    this.setThemeIdForNew = this.setThemeIdForNew.bind(this);
  }

  componentDidMount() {
    const { onTabChange, channelThemes, fetchThemes, modes, fetchModes } =
      this.props;

    onTabChange(0);

    if (mapIsEmpty(channelThemes)) {
      fetchThemes();
    }
    if (mapIsEmpty(modes)) {
      fetchModes();
    }
  }
  componentDidUpdate(prevProps) {
    // Only a min-guest-age rejection moves the user (its message lives on the
    // Sharing tab). Every other failure stays on the current tab so the error
    // shows beside the Save the user just pressed, with all entries intact.
    if (
      !prevProps.errors &&
      this.props.errors &&
      minGuestAgeErrorMessage(this.props.errors)
    ) {
      this.props.onTabChange(
        errorTabIndex(
          this.props.errors,
          this.props.isPhotographer,
          this.props.useMoveboothTheme
        )
      );
    }
  }
  setThemeIdForNew = (val) => {
    this.setState({ themeIdForNew: val });
  };

  render() {
    const {
      fetchClients,
      channelName,
      password,
      clients,
      title,
      isNew,
      isPhotographer,
      currentTab,
      onTabChange,
      openColorPickerModal,
      fetchThemes,
      channelThemes,
      themeId,
      modes,
      modeId,
      disabled,
      nextTab,
      pristine,
      edit,
      channelThemeInstances,
      ownerRole,
      enterpriseAdmins,
      useMoveboothTheme,
      permissionRole,
      validateSlug,
      slugIsValid,
      slugIsValidating,
      withFilter,
      consentAcked,
      setConsentAcked,
      saveError,
      ...formProps
    } = this.props;
    const formsObj = {
      BASIC_INFO: {
        disabled: false,
        title: "Basic Info",
        renderForm: () => (
          <BasicInfoForm
            clients={clients}
            fetchClients={fetchClients}
            channelName={channelName}
            edit={edit}
            isPhotographer={isPhotographer}
            disabled={disabled}
            pristine={pristine}
            enterpriseAdmins={enterpriseAdmins}
            password={password}
            nextTab={nextTab}
            isNew={isNew}
            permissionRole={permissionRole}
            slugIsValid={slugIsValid}
            slugIsValidating={slugIsValidating}
            isPhotographerOwner={
              ownerRole ? ownerRole === "photographer" : isPhotographer
            }
            {...formProps}
          />
        ),
      },
      PHOTO_THEME: {
        title: "Theme",
        renderForm: () => (
          <PhotographerThemeForm
            fetchThemes={fetchThemes}
            channelThemes={channelThemes}
            themeId={themeId}
            setThemeIdFunction={this.setThemeIdForNew}
            edit={edit}
            isNew={isNew}
            channelThemeInstances={channelThemeInstances}
            nextTab={nextTab}
            permissionRole={permissionRole}
            {...formProps}
          />
        ),
      },
      ENTERPRISE_THEME: {
        title: "Logo",
        renderForm: () => (
          <EnterpriseThemeForm
            pristine={pristine}
            isNew={isNew}
            permissionRole={permissionRole}
            openColorPickerModal={openColorPickerModal}
            {...formProps}
          />
        ),
      },
      LOGO_THEME: {
        title: "Logo",
        renderForm: () => (
          <LogoForm
            nextTab={nextTab}
            channelThemes={channelThemes}
            themeId={themeId}
            edit={edit}
            isNew={isNew}
            pristine={pristine}
            permissionRole={permissionRole}
            channelThemeInstances={channelThemeInstances}
            openColorPickerModal={openColorPickerModal}
            filterEnabled={withFilter}
            {...formProps}
          />
        ),
      },
      PHOTO_OPTIONS: {
        title: "Photos",
        renderForm: () => (
          <PhotoOptionsForm
            channelId={this.props.channelId}
            consentAcked={consentAcked}
            setConsentAcked={setConsentAcked}
            persistedModeIds={persistedModeIds(this.props.initialValues)}
            ownsChannel={ownsChannel(this.props.initialValues)}
            channelThemes={channelThemes}
            modes={modes}
            modeId={modeId}
            themeId={themeId}
            isNew={isNew}
            edit={edit}
            nextTab={nextTab}
            permissionRole={permissionRole}
            channelThemeInstances={channelThemeInstances}
            {...formProps}
          />
        ),
      },
      SHARE_OPTIONS: {
        title: "Sharing",
        renderForm: () => (
          <ShareOptionsForm
            isPhotographer={isPhotographer}
            isNew={isNew}
            nextTab={nextTab}
            edit={edit}
            permissionRole={permissionRole}
            useMoveboothTheme={useMoveboothTheme}
            {...formProps}
          />
        ),
      },
      BRANDING: {
        title: "Branding",
        renderForm: () => (
          <BrandingForm
            isPhotographer={isPhotographer}
            isNew={isNew}
            edit={edit}
            nextTab={nextTab}
            permissionRole={permissionRole}
            openColorPickerModal={openColorPickerModal}
            {...formProps}
          />
        ),
      },
      MESSAGING: {
        title: "Messaging",
        renderForm: () => (
          <MessagingOptionForm
            isNew={isNew}
            edit={edit}
            nextTab={nextTab}
            permissionRole={permissionRole}
            openColorPickerModal={openColorPickerModal}
            {...formProps}
          />
        ),
      },
      TERMS: {
        title: "Terms",
        renderForm: () => (
          <TermsForm
            isNew={isNew}
            edit={edit}
            nextTab={nextTab}
            permissionRole={permissionRole}
            openColorPickerModal={openColorPickerModal}
            {...formProps}
          />
        ),
      },
    };

    const tabs = tabNamesFor(isPhotographer, useMoveboothTheme).map(
      (formName) => formsObj[formName]
    );
    return (
      <FormTabs
        formTitle={title}
        currentTab={currentTab}
        onTabChange={onTabChange}
        tabs={tabs}
        isNew={isNew}
        edit={edit}
        pristine={pristine}
        afterForm={
          saveError && !minGuestAgeErrorMessage(saveError) ? (
            <InlineNote id="channel-save-error" role="alert" tone="error">
              {saveErrorMessage(saveError)}
            </InlineNote>
          ) : null
        }
        onCancel={() => {
          if (confirm("All unsaved changes will be lost.")) {
            this.props.navigate("/");
          }
        }}
      />
    );
  }
}

const mapStateToProps = createStructuredSelector({
  permissionRole: (state) => state.app.user.permission_role,
  clients: selectClients(),
  currentTab: selectCurrentTab(),
  isPhotographer: selectIsPhotographer(),
  isFetching: selectIsFetching(),
  channelThemes: selectChannelThemes(),
  modes: selectModes(),
  enterpriseAdmins: selectEnterpriseAdmins(),
  errors: selectChannelErrors(),
  slugIsValid: selectChannelSlugIsValid(),
  saveError: selectSaveError(),
  isSaving: selectIsSaving(),
  slugIsValidating: (state) => state.channels.slugIsValidating,
});

const mapDispatchToProps = (dispatch, ownProps) => ({
  fetchClients: (q) => dispatch(fetchClientsRequest({ q })),
  fetchThemes: () => dispatch(fetchThemesRequest()),
  fetchModes: () => dispatch(fetchModesRequest()),
  clearSaveError: () => dispatch(clearSaveError()),
  onTabChange: (tab) => dispatch(changeTab(tab)),
  nextTab: (tabIndex) => dispatch(changeTab(tabIndex)),
  openColorPickerModal: (modalProps) =>
    dispatch(setModal({ modalType: COLOR_PICKER, modalProps })),
  onShrinkChange: (name, shrink) => {
    if (ownProps?.form?.setValue) {
      ownProps.form.setValue(name, shrink);
    }
  },
});

const Forms = (props) => {
  const form = props.form;
  const initialValues = props.initialValues;

  const values = useWatch({ control: form.control });
  const channelName = values["name"];
  const password = values["password"];
  const modeId = values["mode_id"];
  const useMoveboothTheme = values["useMoveboothTheme"];
  const withFilter = values["with_photo_filter"];

  // BIPA consent gate — owned here, at the one point every tab's submit
  // passes through, and threaded down to PhotoOptionsForm (which renders the
  // notice + checkbox). See guardChannelSubmit above for why.
  const [consentAcked, setConsentAcked] = useState(false);
  const aiPersisted = useMemo(
    () =>
      aiModeSelected(
        selectedModeShortnames(
          captureModes,
          props.modes,
          persistedModeIds(initialValues)
        )
      ),
    [props.modes, initialValues]
  );
  const photosTabIndex = tabNamesFor(
    props.isPhotographer,
    useMoveboothTheme
  ).indexOf("PHOTO_OPTIONS");
  const guardedOnSubmit = guardChannelSubmit({
    onSubmit: props.onSubmit,
    modes: props.modes,
    aiPersisted,
    consentAcked,
    goToPhotosTab: () => props.onTabChange(photosTabIndex),
  });

  // Redundant safety net for tab reset — BaseForm.componentDidMount also calls
  // onTabChange(0), but this covers reconciliation edge cases where Forms
  // remounts without BaseForm remounting.
  useEffect(() => {
    if (props.onTabChange) {
      props.onTabChange(0);
    }
    // A new form session starts without the last form's save rejection.
    if (props.clearSaveError) props.clearSaveError();
  }, []);

  useMinGuestAgeServerError(form, props.errors, true);

  useEffect(() => {
    if (initialValues) {
      Object.keys(initialValues).map((name) => {
        form.setValue(name, initialValues[name]);
      });
    }
  }, []);

  return (
    <BaseForm
      channelName={channelName}
      password={password}
      modeId={modeId}
      useMoveboothTheme={useMoveboothTheme}
      withFilter={withFilter}
      {...props}
      // Save stays off, and a second submit is ignored, while a create or edit
      // is in flight: the saga cancels the first request's handler but not the
      // HTTP request, so a repeat could create twice or fail on a taken link.
      submitting={Boolean(props.submitting || props.isSaving)}
      onSubmit={(...args) => (props.isSaving ? undefined : guardedOnSubmit(...args))}
      consentAcked={consentAcked}
      setConsentAcked={setConsentAcked}
      form={form}
    />
  );
};
export default connect(mapStateToProps, mapDispatchToProps)(withRouter(Forms));
